/**
 * compileSurvey.js
 *
 * Centralized "graph → kernel descriptor" pipeline: expand embedded packages,
 * normalize, order (flow + BFS), and build the descriptor. Mirrors the sequence
 * the Toolbar's cloud-save runs, extracted so Save-as-Package (WP-5) can compile
 * a package's own standalone descriptor with the exact same semantics.
 *
 * A package graph contains no package nodes (nesting is disallowed), so
 * expandPackageNodes is a no-op there — but running it keeps this one pipeline
 * authoritative for every caller.
 */

const { buildNormalizedGraph, buildFlowFromGraph, bfsOrder, normalizePriority } = require('./graphNormalize');
const { buildDescriptorJSON, deriveMediaRefsFromDescriptor } = require('./buildDescriptorJSON');
const { expandPackageNodes } = require('./packageExpansion');

/**
 * @param {Object} params
 * @param {Array}  params.nodes      - authored React Flow nodes
 * @param {Array}  params.edges      - authored React Flow edges
 * @param {Object} params.surveyData - survey-level metadata (title, etc.)
 * @returns {{ descriptor, designerGraph, mediaRefs }}
 */
function compileSurvey({ nodes = [], edges = [], surveyData = {} }) {
  const compiled = expandPackageNodes(nodes, edges);
  const graph = buildNormalizedGraph(compiled.nodes, compiled.edges);

  let flowNormalized = buildFlowFromGraph({ edgesClean: graph.edgesClean, startNodeId: graph.startNodeId });
  const startId = graph.startNodeId;
  flowNormalized = flowNormalized.map((e) => (e.from === 'START' ? { ...e, from: startId } : e));

  const flowSorted = [...flowNormalized].sort((a, b) => {
    if (a.from < b.from) return -1;
    if (a.from > b.from) return 1;
    const pa = normalizePriority(a.priority), pb = normalizePriority(b.priority);
    if (pa !== pb) return pb - pa;
    if (a.to < b.to) return -1;
    if (a.to > b.to) return 1;
    return 0;
  });

  const bfsIds = bfsOrder(graph.startNodeId, graph.adjacency);
  const bfsPos = new Map(bfsIds.map((id, idx) => [id, idx]));
  const sortedQuestionNodes = compiled.nodes
    .filter((n) => ['question', 'start', 'end', 'decision', 'media', 'pleasant'].includes(n.type))
    .sort((a, b) => {
      const ia = bfsPos.has(a.id) ? bfsPos.get(a.id) : Number.MAX_SAFE_INTEGER;
      const ib = bfsPos.has(b.id) ? bfsPos.get(b.id) : Number.MAX_SAFE_INTEGER;
      if (ia !== ib) return ia - ib;
      return String(a.id).localeCompare(String(b.id));
    });

  const descriptor = buildDescriptorJSON({
    sortedQuestionNodes,
    edges: compiled.edges,
    surveyData,
    flowSorted,
  });

  const designerGraph = {
    nodes: (nodes || []).map((n) => ({ id: n.id, position: n.position })),
    edges: (graph.edgesClean || []).map((e) => ({ id: e.id, source: e.source, target: e.target })),
  };

  return { descriptor, designerGraph, mediaRefs: deriveMediaRefsFromDescriptor(descriptor) };
}

module.exports = { compileSurvey };
module.exports.default = compileSurvey;
